import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Link, useNavigate, Navigate, useParams, useLocation, Outlet } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { shopApi, getShopToken, getShopAccount, setShopAccount, shopLogout,
         getCart, addToCart, updateCartQty, removeFromCart, computeLineTotals, saveCart } from "@/lib/shopApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingCart, LogOut, Package, ClipboardList, Store, ArrowRight, Trash2, Plus, Minus, CheckCircle2, Clock, XCircle } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/* --------------------------- ROUTER ROOT ---------------------------- */
export default function ShopApp() {
  return (
    <>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/" element={<ShopLayout />}>
          <Route index element={<Navigate to="/shop/catalog" replace />} />
          <Route path="catalog" element={<ShopCatalog />} />
          <Route path="cart" element={<ShopCart />} />
          <Route path="checkout" element={<RequireShopAuth><ShopCheckout /></RequireShopAuth>} />
          <Route path="orders" element={<RequireShopAuth><ShopOrders /></RequireShopAuth>} />
          <Route path="orders/:oid" element={<RequireShopAuth><ShopOrderDetail /></RequireShopAuth>} />
          <Route path="account" element={<RequireShopAuth><ShopAccount /></RequireShopAuth>} />
          <Route path="login" element={<ShopLogin />} />
          <Route path="signup" element={<ShopSignup />} />
          <Route path="*" element={<Navigate to="/shop/catalog" replace />} />
        </Route>
      </Routes>
    </>
  );
}

function RequireShopAuth({ children }) {
  const loc = useLocation();
  if (!getShopToken()) return <Navigate to="/shop/login" state={{ from: loc.pathname }} replace />;
  return children;
}

/* --------------------------- LAYOUT ---------------------------- */
function ShopLayout() {
  const nav = useNavigate();
  const [account, setAccount] = useState(getShopAccount());
  const [cartCount, setCartCount] = useState(getCart().length);
  useEffect(() => {
    const t = setInterval(() => setCartCount(getCart().length), 500);
    return () => clearInterval(t);
  }, []);
  const logout = () => { shopLogout(); setAccount(null); nav("/shop/login"); };

  return (
    <div className="min-h-screen bg-[#FAF6EE]">
      <header className="border-b border-[#EAE5D9] bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/shop/catalog" className="flex items-center gap-2" data-testid="shop-logo">
            <div className="w-9 h-9 bg-[#C05621] text-white rounded-md flex items-center justify-center font-bold font-[Outfit]">B</div>
            <div className="leading-tight">
              <div className="font-[Outfit] text-lg font-semibold text-[#2C1810]">Bakemill Wholesale</div>
              <div className="text-[10px] uppercase tracking-wider text-[#8B7355]">B2B Portal</div>
            </div>
          </Link>
          <nav className="ml-auto flex items-center gap-1 text-sm">
            <NavLink to="/shop/catalog" label="Catalog" icon={Package} testid="shop-nav-catalog" />
            {account && <NavLink to="/shop/orders" label="Orders" icon={ClipboardList} testid="shop-nav-orders" />}
            <Link to="/shop/cart" className="relative px-3 py-2 rounded-md hover:bg-[#FAF6EE]" data-testid="shop-nav-cart">
              <ShoppingCart className="w-5 h-5 inline" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#C05621] text-white text-[10px] px-1.5 py-0.5 rounded-full">{cartCount}</span>
              )}
            </Link>
            {account ? (
              <>
                <Link to="/shop/account" className="px-3 py-2 text-sm text-[#5C4033] hover:underline" data-testid="shop-nav-account">
                  {account.business_name}
                </Link>
                <button onClick={logout} className="text-xs text-[#C05621] hover:underline flex items-center gap-1" data-testid="shop-nav-logout">
                  <LogOut className="w-3 h-3" /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/shop/login" className="px-3 py-2 text-sm" data-testid="shop-nav-login">Login</Link>
                <Link to="/shop/signup" className="px-3 py-2 text-sm bg-[#2C1810] text-white rounded-md hover:bg-[#3E2822]" data-testid="shop-nav-signup">
                  Get access
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Outlet />
      </main>
      <footer className="border-t border-[#EAE5D9] mt-16 py-6 text-center text-xs text-[#8B7355]">
        © Bakemill Foods Private Limited · Wholesale portal · Powered by our ERP
      </footer>
    </div>
  );
}

function NavLink({ to, label, icon: Icon, testid }) {
  return (
    <Link to={to} className="px-3 py-2 rounded-md hover:bg-[#FAF6EE] text-sm flex items-center gap-1.5" data-testid={testid}>
      <Icon className="w-4 h-4" /> {label}
    </Link>
  );
}

/* --------------------------- LOGIN ---------------------------- */
function ShopLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await shopApi.post("/shop/login", { email, password });
      localStorage.setItem("bmf_shop_token", res.data.token);
      setShopAccount(res.data.account);
      toast.success(`Welcome back, ${res.data.account.business_name}`);
      nav(loc.state?.from || "/shop/catalog");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-md mx-auto mt-16">
      <Card className="border-[#EAE5D9]">
        <CardHeader>
          <CardTitle className="font-[Outfit] text-2xl">B2B Login</CardTitle>
          <p className="text-sm text-[#8B7355]">Wholesale accounts only. Retail customers should visit our stores.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Business email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" data-testid="shop-login-email" />
            </div>
            <div>
              <Label>Password</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} required type="password" data-testid="shop-login-password" />
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="shop-login-submit">
              {busy ? "Logging in…" : "Login"}
            </Button>
            <div className="text-center text-xs text-[#8B7355]">
              New here? <Link to="/shop/signup" className="text-[#C05621] hover:underline">Request access</Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

/* --------------------------- SIGNUP ---------------------------- */
function ShopSignup() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", business_name: "", contact_name: "",
    phone: "", gstin: "", address: "", city: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await shopApi.post("/shop/signup", form);
      toast.success("Registered! We'll email you once approved.");
      nav("/shop/login");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="max-w-xl mx-auto mt-8">
      <Card className="border-[#EAE5D9]">
        <CardHeader>
          <CardTitle className="font-[Outfit] text-2xl">Request wholesale access</CardTitle>
          <p className="text-sm text-[#8B7355]">Cafés, hotels, distributors and event caterers. Our team reviews within 24 hours.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Business name *</Label>
                <Input value={form.business_name} onChange={upd("business_name")} required data-testid="shop-signup-biz" />
              </div>
              <div>
                <Label>Contact name *</Label>
                <Input value={form.contact_name} onChange={upd("contact_name")} required data-testid="shop-signup-contact" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input value={form.email} onChange={upd("email")} type="email" required data-testid="shop-signup-email" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={form.phone} onChange={upd("phone")} required data-testid="shop-signup-phone" />
              </div>
              <div>
                <Label>GSTIN</Label>
                <Input value={form.gstin} onChange={upd("gstin")} data-testid="shop-signup-gstin" />
              </div>
              <div>
                <Label>City</Label>
                <Input value={form.city} onChange={upd("city")} data-testid="shop-signup-city" />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Textarea rows={2} value={form.address} onChange={upd("address")} data-testid="shop-signup-address" />
              </div>
              <div className="col-span-2">
                <Label>Password (min 6) *</Label>
                <Input value={form.password} onChange={upd("password")} type="password" required minLength={6} data-testid="shop-signup-password" />
              </div>
              <div className="col-span-2">
                <Label>Notes to us</Label>
                <Textarea rows={2} value={form.notes} onChange={upd("notes")} placeholder="Volume estimate, occasion, etc." data-testid="shop-signup-notes" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="shop-signup-submit">
              {busy ? "Submitting…" : "Request access"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

/* --------------------------- CATALOG ---------------------------- */
function ShopCatalog() {
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    const path = getShopToken() ? "/shop/catalog/authed" : "/shop/catalog";
    shopApi.get(path).then((r) => setProducts(r.data)).catch((e) => toast.error("Failed to load catalog"));
  }, []);
  const filtered = useMemo(() => {
    if (!products) return [];
    const term = q.trim().toLowerCase();
    return term ? products.filter((p) => (p.name + p.sku + (p.category || "")).toLowerCase().includes(term)) : products;
  }, [q, products]);
  if (!products) return <div className="text-[#8B7355]">Loading catalog…</div>;
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="uc-label text-[#8B7355]">Wholesale catalog</div>
          <h1 className="font-[Outfit] text-3xl font-semibold text-[#2C1810]">Fresh from our bakery to yours</h1>
        </div>
        <Input placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="shop-catalog-search" />
      </div>
      {filtered.length === 0 ? (
        <div className="p-16 text-center text-[#8B7355]">
          <Store className="w-10 h-10 mx-auto mb-4 opacity-40" />
          No wholesale products available yet. Check back soon.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shop-catalog-grid">
          {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product }) {
  const [qty, setQty] = useState(1);
  const tiers = (product.b2b_bulk_tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty);
  const applicable = tiers.filter((t) => qty >= Number(t.min_qty)).slice(-1)[0];
  const bulkPct = applicable ? Number(applicable.discount_pct) : 0;
  const custPct = Number(product.customer_discount_pct || 0);
  const unit = Number((product.base_price * (1 - custPct / 100) * (1 - bulkPct / 100)).toFixed(2));

  const add = () => {
    if (!getShopToken()) { toast.info("Please log in to add to cart"); return; }
    addToCart(product, qty);
    toast.success(`Added ${qty} × ${product.name}`);
  };

  return (
    <Card className="border-[#EAE5D9] hover:shadow-md transition" data-testid={`shop-product-${product.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase text-[#8B7355]">{product.category}</div>
            <h3 className="font-[Outfit] text-lg font-semibold text-[#2C1810]">{product.name}</h3>
            <div className="text-xs text-[#8B7355]">SKU {product.sku} · GST {product.gst_rate}%</div>
          </div>
        </div>
        {product.description && <p className="text-xs text-[#5C4033] line-clamp-2">{product.description}</p>}
        <div>
          <div className="flex items-baseline justify-between">
            <div>
              {(bulkPct > 0 || custPct > 0) && (
                <div className="text-xs text-[#8B7355] line-through">{inr(product.base_price)}</div>
              )}
              <div className="text-2xl font-[Outfit] font-bold text-[#C05621]" data-testid={`shop-price-${product.id}`}>{inr(unit)}</div>
              <div className="text-[10px] text-[#8B7355]">per {product.unit || "pcs"}{bulkPct > 0 && ` · ${bulkPct}% bulk off`}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-[#8B7355]">Qty</div>
              <div className="flex items-center gap-1">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-7 h-7 border border-[#EAE5D9] rounded flex items-center justify-center" data-testid={`shop-qty-dec-${product.id}`}><Minus className="w-3 h-3" /></button>
                <Input value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))} className="w-14 h-7 text-center px-1" data-testid={`shop-qty-${product.id}`} />
                <button onClick={() => setQty((q) => q + 1)} className="w-7 h-7 border border-[#EAE5D9] rounded flex items-center justify-center" data-testid={`shop-qty-inc-${product.id}`}><Plus className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        </div>
        {tiers.length > 0 && (
          <div className="text-[10px] text-[#8B7355] flex flex-wrap gap-1">
            <span className="mr-1">Bulk:</span>
            {tiers.map((t, i) => (
              <span key={i} className={`px-1.5 py-0.5 rounded ${qty >= Number(t.min_qty) ? "bg-emerald-100 text-emerald-800" : "bg-[#FAF6EE]"}`}>
                {t.min_qty}+ → -{t.discount_pct}%
              </span>
            ))}
          </div>
        )}
        <Button onClick={add} className="w-full bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid={`shop-add-${product.id}`}>
          <ShoppingCart className="w-3 h-3 mr-1" /> Add to cart
        </Button>
      </CardContent>
    </Card>
  );
}

/* --------------------------- CART ---------------------------- */
function ShopCart() {
  const [cart, setCart] = useState(getCart());
  const nav = useNavigate();
  const totals = useMemo(() => {
    let subtotal = 0, gst = 0;
    for (const item of cart) {
      const t = computeLineTotals(item);
      subtotal += t.line;
      gst += t.gst;
    }
    return { subtotal, gst, grand: subtotal + gst };
  }, [cart]);

  const setQty = (pid, q) => setCart(updateCartQty(pid, q));
  const remove = (pid) => setCart(removeFromCart(pid));
  const clearAll = () => { saveCart([]); setCart([]); };

  if (cart.length === 0) {
    return (
      <div className="p-16 text-center text-[#8B7355]">
        <ShoppingCart className="w-10 h-10 mx-auto mb-4 opacity-40" />
        Your cart is empty. <Link to="/shop/catalog" className="text-[#C05621] hover:underline">Browse catalog</Link>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div className="space-y-3" data-testid="shop-cart-items">
        <h1 className="font-[Outfit] text-2xl font-semibold text-[#2C1810]">Cart · {cart.length} lines</h1>
        {cart.map((item) => {
          const t = computeLineTotals(item);
          return (
            <Card key={item.product_id} className="border-[#EAE5D9]" data-testid={`shop-cart-line-${item.product_id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="font-medium">{item.name}</div>
                  <div className="text-xs text-[#8B7355]">SKU {item.sku}{t.bulkPct > 0 && ` · ${t.bulkPct}% bulk off`}{t.custPct > 0 && ` · ${t.custPct}% partner rate`}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setQty(item.product_id, Number(item.quantity) - 1)} className="w-7 h-7 border rounded" data-testid={`shop-cart-dec-${item.product_id}`}><Minus className="w-3 h-3 mx-auto" /></button>
                  <Input value={item.quantity} onChange={(e) => setQty(item.product_id, Math.max(1, Number(e.target.value) || 1))} className="w-16 h-7 text-center" />
                  <button onClick={() => setQty(item.product_id, Number(item.quantity) + 1)} className="w-7 h-7 border rounded" data-testid={`shop-cart-inc-${item.product_id}`}><Plus className="w-3 h-3 mx-auto" /></button>
                </div>
                <div className="w-28 text-right">
                  <div className="text-xs text-[#8B7355]">{inr(t.unit)} each</div>
                  <div className="font-semibold">{inr(t.total)}</div>
                </div>
                <button onClick={() => remove(item.product_id)} className="text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`shop-cart-remove-${item.product_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </CardContent>
            </Card>
          );
        })}
        <button onClick={clearAll} className="text-xs text-red-600 hover:underline" data-testid="shop-cart-clear">Clear cart</button>
      </div>
      <Card className="border-[#EAE5D9] h-fit sticky top-24">
        <CardHeader><CardTitle className="font-[Outfit]">Summary</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Row label="Subtotal" value={inr(totals.subtotal)} />
          <Row label="GST" value={inr(totals.gst)} />
          <div className="border-t pt-2 flex items-baseline justify-between">
            <span className="font-medium">Total (credit)</span>
            <span className="text-2xl font-[Outfit] font-bold text-[#C05621]" data-testid="shop-cart-total">{inr(totals.grand)}</span>
          </div>
          <Button onClick={() => nav("/shop/checkout")} className="w-full bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="shop-cart-checkout">
            Proceed to checkout <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
          <p className="text-[10px] text-[#8B7355] text-center">Orders bill on credit terms. Our team confirms within 4 hours.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-baseline justify-between text-sm"><span className="text-[#5C4033]">{label}</span><span>{value}</span></div>;
}

/* --------------------------- CHECKOUT ---------------------------- */
function ShopCheckout() {
  const [cart] = useState(getCart());
  const [account, setAccount] = useState(getShopAccount());
  const [me, setMe] = useState(null);
  const [branches, setBranches] = useState([]);
  const [ful, setFul] = useState("delivery");
  const [pickupBranch, setPickupBranch] = useState("");
  const [addr, setAddr] = useState({ address: "", city: "", pincode: "", date: "" });
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    shopApi.get("/shop/me").then((r) => { setMe(r.data); if (r.data.customer) setAddr((a) => ({ ...a, city: r.data.customer.city || "" })); }).catch(() => {});
    // branches endpoint may or may not be open — try public first
    shopApi.get("/shop/branches").then((r) => setBranches(r.data)).catch(() => setBranches([]));
  }, []);

  const totals = useMemo(() => {
    let subtotal = 0, gst = 0;
    for (const item of cart) {
      const t = computeLineTotals(item);
      subtotal += t.line;
      gst += t.gst;
    }
    return { subtotal, gst, grand: subtotal + gst };
  }, [cart]);

  const submit = async () => {
    if (!me || me.account.status !== "approved") {
      toast.error(`Your account is ${me?.account?.status || "not approved"} — cannot place orders yet.`);
      return;
    }
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    if (ful === "pickup" && !pickupBranch) { toast.error("Pick a branch"); return; }
    if (ful === "delivery" && (!addr.address || !addr.date)) { toast.error("Delivery address + date required"); return; }
    setBusy(true);
    try {
      const payload = {
        items: cart.map((c) => ({ product_id: c.product_id, quantity: Number(c.quantity) })),
        fulfilment_type: ful,
        pickup_branch_id: ful === "pickup" ? pickupBranch : null,
        delivery_address: ful === "delivery" ? addr.address : null,
        delivery_city: ful === "delivery" ? addr.city : null,
        delivery_pincode: ful === "delivery" ? addr.pincode : null,
        delivery_date: ful === "delivery" ? addr.date : null,
        notes,
      };
      const res = await shopApi.post("/shop/orders", payload);
      saveCart([]);
      toast.success(`Order ${res.data.number} placed! Our team will confirm shortly.`);
      nav(`/shop/orders/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order failed");
    } finally { setBusy(false); }
  };

  const pending = me && me.account.status !== "approved";

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      <div className="space-y-4">
        <h1 className="font-[Outfit] text-2xl font-semibold text-[#2C1810]">Checkout</h1>
        {pending && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800" data-testid="shop-checkout-pending">
            Your account is <b>{me?.account?.status}</b>. Orders open after our team approves your business.
          </div>
        )}
        <Card className="border-[#EAE5D9]">
          <CardHeader><CardTitle className="font-[Outfit] text-lg">Fulfilment</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setFul("delivery")} className={`p-3 border rounded-md text-left ${ful === "delivery" ? "border-[#C05621] bg-[#FFF8F1]" : "border-[#EAE5D9]"}`} data-testid="shop-fulfilment-delivery">
                <div className="font-semibold">Delivery</div>
                <div className="text-xs text-[#8B7355]">We deliver to your address</div>
              </button>
              <button onClick={() => setFul("pickup")} className={`p-3 border rounded-md text-left ${ful === "pickup" ? "border-[#C05621] bg-[#FFF8F1]" : "border-[#EAE5D9]"}`} data-testid="shop-fulfilment-pickup">
                <div className="font-semibold">Pickup</div>
                <div className="text-xs text-[#8B7355]">Collect from a Bakemill branch</div>
              </button>
            </div>
            {ful === "pickup" && (
              <div>
                <Label>Pickup branch</Label>
                <Select value={pickupBranch} onValueChange={setPickupBranch}>
                  <SelectTrigger data-testid="shop-pickup-branch"><SelectValue placeholder="Choose a branch" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.location ? ` — ${b.location}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {ful === "delivery" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Delivery address *</Label>
                  <Textarea rows={2} value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} data-testid="shop-delivery-address" />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="shop-delivery-city" />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} data-testid="shop-delivery-pincode" />
                </div>
                <div className="col-span-2">
                  <Label>Preferred delivery date *</Label>
                  <Input type="date" value={addr.date} onChange={(e) => setAddr({ ...addr, date: e.target.value })} data-testid="shop-delivery-date" />
                </div>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="shop-order-notes" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#EAE5D9] h-fit sticky top-24">
        <CardHeader><CardTitle className="font-[Outfit]">Order summary</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {cart.map((c) => {
            const t = computeLineTotals(c);
            return (
              <div key={c.product_id} className="text-sm flex justify-between">
                <span className="text-[#5C4033]">{c.name} × {c.quantity}</span>
                <span>{inr(t.total)}</span>
              </div>
            );
          })}
          <div className="border-t pt-2 space-y-1">
            <Row label="Subtotal" value={inr(totals.subtotal)} />
            <Row label="GST" value={inr(totals.gst)} />
          </div>
          <div className="border-t pt-2 flex items-baseline justify-between">
            <span className="font-medium">Total (credit)</span>
            <span className="text-2xl font-[Outfit] font-bold text-[#C05621]" data-testid="shop-checkout-total">{inr(totals.grand)}</span>
          </div>
          <Button disabled={busy || pending} onClick={submit} className="w-full bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="shop-checkout-submit">
            {busy ? "Placing…" : "Place order"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/* --------------------------- ORDERS ---------------------------- */
function ShopOrders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { shopApi.get("/shop/orders").then((r) => setOrders(r.data)).catch(() => toast.error("Failed to load orders")); }, []);
  if (!orders) return <div className="text-[#8B7355]">Loading…</div>;
  if (orders.length === 0) return <div className="p-16 text-center text-[#8B7355]">No orders yet. <Link to="/shop/catalog" className="text-[#C05621] hover:underline">Start shopping</Link></div>;
  return (
    <div className="space-y-3">
      <h1 className="font-[Outfit] text-2xl font-semibold text-[#2C1810]">My orders</h1>
      <div className="space-y-2" data-testid="shop-orders-list">
        {orders.map((o) => (
          <Link to={`/shop/orders/${o.id}`} key={o.id} className="block" data-testid={`shop-order-${o.id}`}>
            <Card className="border-[#EAE5D9] hover:shadow-sm">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="font-mono text-sm">{o.number}</div>
                  <div className="text-xs text-[#8B7355]">{new Date(o.created_at).toLocaleString("en-IN")} · {o.items.length} items · {o.fulfilment_type}</div>
                </div>
                <StatusBadge status={o.status} />
                <div className="text-right">
                  <div className="font-semibold text-[#C05621]">{inr(o.grand_total)}</div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

function ShopOrderDetail() {
  const { oid } = useParams();
  const [order, setOrder] = useState(null);
  useEffect(() => { shopApi.get(`/shop/orders/${oid}`).then((r) => setOrder(r.data)).catch(() => toast.error("Failed")); }, [oid]);
  if (!order) return <div className="text-[#8B7355]">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="shop-order-detail">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="uc-label text-[#8B7355]">Order</div>
          <h1 className="font-[Outfit] text-2xl font-semibold text-[#2C1810]">{order.number}</h1>
          <div className="text-xs text-[#8B7355]">{new Date(order.created_at).toLocaleString("en-IN")}</div>
        </div>
        <StatusBadge status={order.status} />
      </div>
      <Card className="border-[#EAE5D9]">
        <CardHeader><CardTitle className="font-[Outfit] text-lg">Line items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
              <tr><th className="text-left px-4 py-2">Product</th><th className="text-right px-4 py-2">Qty</th><th className="text-right px-4 py-2">Unit</th><th className="text-right px-4 py-2">Total</th></tr>
            </thead>
            <tbody>
              {order.items.map((it, i) => (
                <tr key={i} className="border-t border-[#EAE5D9]">
                  <td className="px-4 py-2">{it.name}<div className="text-[10px] text-[#8B7355]">SKU {it.sku}{it.bulk_discount_pct > 0 && ` · ${it.bulk_discount_pct}% bulk off`}</div></td>
                  <td className="px-4 py-2 text-right">{it.quantity}</td>
                  <td className="px-4 py-2 text-right">{inr(it.unit_price)}</td>
                  <td className="px-4 py-2 text-right">{inr(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#FAF6EE]">
              <tr><td colSpan={3} className="px-4 py-2 text-right text-sm text-[#5C4033]">Subtotal</td><td className="px-4 py-2 text-right">{inr(order.subtotal)}</td></tr>
              <tr><td colSpan={3} className="px-4 py-2 text-right text-sm text-[#5C4033]">GST</td><td className="px-4 py-2 text-right">{inr(order.gst_total)}</td></tr>
              <tr className="font-semibold"><td colSpan={3} className="px-4 py-2 text-right text-[#2C1810]">Total (credit)</td><td className="px-4 py-2 text-right text-[#C05621]">{inr(order.grand_total)}</td></tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>
      <Card className="border-[#EAE5D9]">
        <CardHeader><CardTitle className="font-[Outfit] text-lg">Fulfilment</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1">
          <div>{order.fulfilment_type === "pickup" ? `Pickup — ${order.pickup_branch_name}` : `Delivery on ${order.delivery_date}`}</div>
          {order.delivery_address && <div className="text-[#8B7355]">{order.delivery_address}, {order.delivery_city} {order.delivery_pincode}</div>}
          {order.notes && <div className="text-[#8B7355] italic">"{order.notes}"</div>}
          {order.sale_number && <div className="text-emerald-700 text-xs">Confirmed as invoice {order.sale_number}</div>}
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { icon: Clock, cls: "bg-amber-100 text-amber-800" },
    confirmed: { icon: CheckCircle2, cls: "bg-blue-100 text-blue-800" },
    ready: { icon: Package, cls: "bg-indigo-100 text-indigo-800" },
    dispatched: { icon: Package, cls: "bg-purple-100 text-purple-800" },
    delivered: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800" },
    cancelled: { icon: XCircle, cls: "bg-red-100 text-red-800" },
  };
  const { icon: Icon, cls } = map[status] || map.pending;
  return <Badge className={`${cls} border-0 flex items-center gap-1`}><Icon className="w-3 h-3" />{status}</Badge>;
}

/* --------------------------- ACCOUNT ---------------------------- */
function ShopAccount() {
  const [me, setMe] = useState(null);
  useEffect(() => { shopApi.get("/shop/me").then((r) => { setMe(r.data); setShopAccount(r.data.account); }).catch(() => {}); }, []);
  if (!me) return <div className="text-[#8B7355]">Loading…</div>;
  const a = me.account;
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <h1 className="font-[Outfit] text-2xl font-semibold text-[#2C1810]">My account</h1>
      <Card className="border-[#EAE5D9]">
        <CardContent className="p-6 space-y-2 text-sm">
          <Row label="Business" value={a.business_name} />
          <Row label="Contact" value={a.contact_name} />
          <Row label="Email" value={a.email} />
          <Row label="Phone" value={a.phone} />
          <Row label="GSTIN" value={a.gstin || "—"} />
          <Row label="Status" value={<StatusBadge status={a.status} />} />
          {me.customer && (
            <>
              <div className="border-t my-2" />
              <Row label="Partner discount" value={`${me.customer.discount_pct || 0}%`} />
              <Row label="Credit limit" value={inr(me.credit_limit)} />
              <Row label="Outstanding" value={inr(me.outstanding)} />
              <Row label="Loyalty points" value={me.loyalty_points} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
